<template>
  <div ref="pageRef" class="index-page">
    <div class="mobile-shell">
      <div class="mobile-bg-glow mobile-bg-glow-1"></div>
      <div class="mobile-bg-glow mobile-bg-glow-2"></div>

      <div class="mobile-screen">
        <template v-if="activeMobileTab === 'discover'">
          <header class="mobile-topbar">
            <div class="mobile-search">
              <font-awesome-icon icon="magnifying-glass" class="mobile-search-icon" />
              <input type="text" placeholder="搜索视频..." class="mobile-search-input" />
            </div>
            <button type="button" class="mobile-avatar-btn">
              <div class="mobile-avatar-thumb">
                <font-awesome-icon icon="seedling" />
              </div>
            </button>
          </header>

          <section class="mobile-hero">
            <h2 class="mobile-hero-title">欢迎回来，小树苗</h2>
            <p class="mobile-hero-subtitle">今天是 {{ currentDate }}，今天也要快乐学习哦！</p>
          </section>

          <section class="mobile-stage-list">
            <article
              v-for="stage in learningStages"
              :key="`mobile-${stage.id}`"
              class="mobile-stage-card"
              :style="{
                '--mobile-stage-color': stage.color,
                '--mobile-stage-bg': stage.bgColor
              }"
              @click="handleStageSelect(stage)"
            >
              <div class="mobile-stage-visual">
                <div class="mobile-stage-icon" :style="{ background: stage.bgColor }">
                  <font-awesome-icon :icon="stage.icon" />
                </div>
              </div>
              <div class="mobile-stage-copy">
                <span class="mobile-stage-age">{{ stage.ageRange }}</span>
                <h3 class="mobile-stage-title">【{{ stage.title }}】</h3>
                <p class="mobile-stage-desc">{{ stage.subtitle }}</p>
              </div>
              <button type="button" class="mobile-stage-play" aria-label="进入阶段">
                <font-awesome-icon icon="play" />
              </button>
            </article>
          </section>
        </template>

        <template v-else-if="activeMobileTab === 'progress'">
          <section class="mobile-progress-head">
            <h2 class="mobile-section-heading">学习进度</h2>
          </section>

          <template v-for="item in progressCards" :key="`mobile-progress-${item.id}`">
            <section v-if="item.id === 'calendar'" class="mobile-panel-card">
              <div class="mobile-panel-head">
                <h3 class="mobile-panel-title">宝贝每日打卡历</h3>
                <span class="mobile-panel-badge">连续打卡 {{ consecutiveCheckInDays }} 天</span>
              </div>

              <div class="mobile-calendar-grid">
                <div v-for="day in weekDays" :key="`mobile-week-${day}`" class="mobile-calendar-weekday">
                  {{ day }}
                </div>
                <div
                  v-for="(date, index) in calendarDays"
                  :key="`mobile-date-${index}`"
                  :class="[
                    'mobile-calendar-day',
                    {
                      'is-today': date === today,
                      'is-checked': checkInDays.includes(date),
                      'is-empty': date === 0
                    }
                  ]"
                >
                  <template v-if="date">
                    <span class="mobile-calendar-number">{{ date }}</span>
                    <font-awesome-icon
                      v-if="checkInDays.includes(date)"
                      icon="star"
                      class="mobile-calendar-star"
                    />
                  </template>
                </div>
              </div>

              <button
                type="button"
                class="mobile-check-in-btn"
                :class="{ checked: hasCheckedInToday }"
                :disabled="hasCheckedInToday"
                @click="handleCheckIn"
              >
                {{ hasCheckedInToday ? '今日已签到' : '签到' }}
              </button>
            </section>

            <section v-else-if="item.id === 'weekly-time'" class="mobile-panel-card">
              <div class="mobile-panel-head mobile-panel-head-chart">
                <h3 class="mobile-panel-title">本周学习时长</h3>
                <span class="mobile-panel-meta">单位: 分钟</span>
              </div>
              <div ref="mobileChartRef" class="mobile-chart-container"></div>
            </section>

            <section v-else-if="item.id === 'vocab'" class="mobile-panel-card mobile-vocab-card">
              <div class="mobile-vocab-deco mobile-vocab-deco-top">
                <font-awesome-icon icon="book-open" />
              </div>
              <h3 class="mobile-panel-title">累计已学习词汇量</h3>
              <p class="mobile-vocab-number">1280</p>
            </section>
          </template>
        </template>

        <template v-else>
          <section class="mobile-profile-top">
            <div class="mobile-profile-avatar">
              <div class="mobile-profile-avatar-inner">
                <font-awesome-icon icon="seedling" />
              </div>
            </div>
            <h2 class="mobile-profile-name">小树苗用户</h2>
            <div class="mobile-membership-card">
              <div class="mobile-membership-copy">
                <div class="mobile-membership-title">
                  <font-awesome-icon icon="crown" class="mobile-membership-crown" />
                  <span>树苗会员</span>
                </div>
                <p class="mobile-membership-expiry">会员有效期至2026/04/24</p>
              </div>
              <button type="button" class="mobile-membership-link" @click="router.push('/vip')">
                会员中心
                <font-awesome-icon icon="chevron-right" />
              </button>
            </div>
          </section>

          <section class="mobile-profile-menu">
            <button
              v-for="item in mobileProfileItems"
              :key="item.id"
              type="button"
              class="mobile-profile-item"
              :class="{ 'is-danger': item.danger }"
              @click="handleProfileItemClick(item)"
            >
              <span class="mobile-profile-item-icon" :style="{ '--profile-accent': item.color }">
                <font-awesome-icon :icon="item.icon" />
              </span>
              <span class="mobile-profile-item-label">{{ item.label }}</span>
              <font-awesome-icon icon="chevron-right" class="mobile-profile-item-arrow" />
            </button>
          </section>
        </template>

        <nav class="mobile-tabbar">
          <button
            v-for="tab in mobileTabs"
            :key="tab.id"
            type="button"
            class="mobile-tabbar-item"
            :class="{ active: activeMobileTab === tab.id }"
            @click="activeMobileTab = tab.id"
          >
            <font-awesome-icon :icon="tab.icon" class="mobile-tabbar-icon" />
            <span>{{ tab.label }}</span>
          </button>
        </nav>
      </div>
    </div>

    <!-- 移动端菜单按钮 -->
    <button class="mobile-menu-btn" @click="showMobileMenu = !showMobileMenu">
      <font-awesome-icon :icon="showMobileMenu ? 'times' : 'bars'" />
    </button>

    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <!-- 主要内容区域 -->
    <div class="content-wrapper">
      <!-- 左侧导航 -->
      <aside class="sidebar" :class="{ 'mobile-open': showMobileMenu }">
        <nav class="nav-card">
          <!-- Logo区域 -->
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <h1 class="brand-name">树苗英语</h1>
          </div>
          
          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id && item.id !== 'logout' }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>

        </nav>
      </aside>

      <!-- 移动端遮罩 -->
      <div v-if="showMobileMenu" class="mobile-overlay" @click="showMobileMenu = false"></div>

        <!-- 右侧主内容 -->
        <div class="right-wrapper">
          <!-- 顶部欢迎栏 -->
          <div class="glass-card welcome-bar">
            <div class="welcome-left">
              <h2 class="welcome-text">
                欢迎回来，小树苗 <span class="wave">👋</span>
              </h2>
              <p class="date-text">今天是 {{ currentDate }}，今天也要快乐学习哦！</p>
            </div>
            <div class="top-membership-card">
              <div class="top-membership-copy">
                <div class="top-membership-title">
                  <font-awesome-icon icon="crown" class="top-membership-crown" />
                  <span>树苗会员</span>
                </div>
                <p class="top-membership-expiry">有效期至 2026/05/25</p>
              </div>
              <button type="button" class="top-membership-btn" @click="router.push('/vip')">
                立即续费
              </button>
            </div>
          </div>

          <!-- 内容区域 -->
          <div class="content-area">
            <!-- 左侧内容 -->
            <div class="left-content">
              <!-- 学习阶段卡片 -->
              <section class="section-card">
                <h3 class="section-title">选择学习阶段</h3>
                <div class="stage-grid">
                  <div
                    v-for="stage in learningStages"
                    :key="stage.id"
                    class="stage-card"
                    :style="{ '--stage-color': stage.color }"
                    @click="handleStageSelect(stage)"
                  >
                    <div class="stage-icon" :style="{ background: stage.bgColor }">
                      <font-awesome-icon :icon="stage.icon" />
                    </div>
                    <div class="stage-info">
                      <h4 class="stage-title">{{ stage.title }}</h4>
                      <p class="stage-desc">{{ stage.subtitle }}</p>
                      <span class="stage-tag" :style="{ background: stage.bgColor, color: stage.color }">
                        {{ stage.ageRange }}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <!-- 数据卡片区域 -->
              <div class="data-grid">
                <div class="glass-card data-card">
                  <h4 class="data-title">累计已学习词汇量</h4>
                  <div class="data-content">
                    <div class="data-number">
                      <span class="number">1280</span>
                      <span class="growth">+85 since last week</span>
                    </div>
                    <div class="data-deco">
                      <font-awesome-icon icon="book-open" />
                    </div>
                  </div>
                </div>

                <div class="glass-card chart-card">
                  <h4 class="data-title">本周学习数据（分钟）</h4>
                  <div ref="chartRef" class="chart-container"></div>
                </div>
              </div>

            </div>

            <!-- 右侧边栏 -->
            <div class="right-panel">
              <!-- 打卡日历 -->
              <div class="glass-card calendar-card">
                <div class="calendar-header">
                  <h4 class="calendar-title">打卡日历</h4>
                  <span class="calendar-month">{{ currentMonth }}</span>
                </div>
                <div class="calendar-grid">
                  <div v-for="day in weekDays" :key="day" class="calendar-weekday">{{ day }}</div>
                  <div
                    v-for="(date, index) in calendarDays"
                    :key="index"
                    :class="['calendar-day', {
                      'is-today': date === today,
                      'is-checked': checkInDays.includes(date),
                      'is-empty': date === 0
                    }]"
                  >
                    {{ date || '' }}
                  </div>
                </div>
                <div class="streak-tip">
                  <div class="streak-main">
                    <font-awesome-icon icon="fire" class="streak-icon" />
                    <div class="streak-info">
                      <span class="streak-count">已连续{{ consecutiveCheckInDays }}天</span>
                      <span class="streak-hint">{{ hasCheckedInToday ? '' : '继续保持哦！' }}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="check-in-btn"
                    :class="{ checked: hasCheckedInToday }"
                    :disabled="hasCheckedInToday"
                    @click="handleCheckIn"
                  >
                    {{ hasCheckedInToday ? '今日已签到' : '签到' }}
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

    <ParentPasswordModal ref="parentPasswordModalRef" @verified="activeNav = 'parent'" />

    <Transition name="checkin-toast">
      <div v-if="checkInToastMessage" class="checkin-toast">
        {{ checkInToastMessage }}
      </div>
    </Transition>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as echarts from 'echarts'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import logo2Image from '../assets/logo2.png'

const AUTH_STORAGE_KEY = 'tree-sprout-auth'
const PARENT_VERIFIED_STORAGE_KEY = 'tree-sprout-parent-verified'
const STAGE_ORDER_STORAGE_KEY = 'tree-sprout-stage-order'
const PROGRESS_ORDER_STORAGE_KEY = 'tree-sprout-progress-card-order'

const router = useRouter()
const route = useRoute()

const today = new Date().getDate()
const currentDate = computed(() => {
  const now = new Date()
  return `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日`
})
const currentMonth = computed(() => {
  const now = new Date()
  return `${now.getMonth() + 1}月`
})
const weekDays = ['日', '一', '二', '三', '四', '五', '六']

const activeNav = ref('home')
const activeMobileTab = ref('discover')
const showMobileMenu = ref(false)
const pageRef = ref(null)
const checkInToastMessage = ref('')
const parentPasswordModalRef = ref(null)
let checkInToastTimer = null

const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看记录', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]

navItems.push({ id: 'logout', label: '登出账号', icon: 'right-from-bracket' })

const mobileTabs = [
  { id: 'discover', label: '发现', icon: 'home' },
  { id: 'progress', label: '打卡', icon: 'calendar-days' },
  { id: 'profile', label: '我的', icon: 'user' },
]

const syncMobileTabFromRoute = () => {
  const tab = typeof route.query.tab === 'string' ? route.query.tab : ''

  if (mobileTabs.some((item) => item.id === tab)) {
    activeMobileTab.value = tab
  }
}

const mobileProfileItems = [
  { id: 'history', label: '观看记录', icon: 'clock-rotate-left', color: '#8db8de' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart', color: '#df89b8' },
  { id: 'settings', label: '偏好设置', icon: 'gear', color: '#9b91d8' },
  { id: 'parent', label: '家长模式', icon: 'lock', color: '#d7a15f' },
]

mobileProfileItems.push({ id: 'logout', label: '登出账号', icon: 'right-from-bracket', color: '#e17373', danger: true })

const handleLogout = () => {
  window.localStorage.removeItem(AUTH_STORAGE_KEY)
  window.sessionStorage.removeItem(AUTH_STORAGE_KEY)
  window.sessionStorage.removeItem(PARENT_VERIFIED_STORAGE_KEY)
  router.replace('/')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  showMobileMenu.value = false

  if (item.id === 'logout') {
    handleLogout()
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
    return
  }

  activeNav.value = item.id
}

const handleProfileItemClick = (item) => {
  if (item.id === 'logout') {
    handleLogout()
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'progress') {
    router.replace({
      path: '/home',
      query: {
        ...route.query,
        tab: 'progress'
      }
    })
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
    return
  }

  activeNav.value = item.id
}

const handleStageSelect = (stage) => {
  router.push({
    path: '/category',
    query: { stage: stage.id }
  })
}

const defaultLearningStages = [
  { id: 'sense', title: '启蒙感知', subtitle: '通过趣味动画和儿歌培养英语兴趣', ageRange: '0-3岁', icon: 'palette', color: '#5B8FF9', bgColor: 'rgba(91, 143, 249, 0.15)' },
  { id: 'sprout', title: '语言萌芽', subtitle: '基础词汇和简单句型学习', ageRange: '3-6岁', icon: 'sprout', color: '#4CAF50', bgColor: 'rgba(76, 175, 80, 0.15)' },
  { id: 'expression', title: '表达发展', subtitle: '口语表达和简单对话练习', ageRange: '6-9岁', icon: 'comments', color: '#FFB020', bgColor: 'rgba(255, 176, 32, 0.15)' },
  { id: 'ability', title: '能力提升', subtitle: '阅读理解和简单书写', ageRange: '9-12岁', icon: 'book', color: '#9C6ADE', bgColor: 'rgba(156, 106, 222, 0.15)' },
]

const getSavedStageOrder = () => {
  const defaultIds = defaultLearningStages.map((stage) => stage.id)

  try {
    const savedOrder = JSON.parse(window.localStorage.getItem(STAGE_ORDER_STORAGE_KEY) || '[]')
    const validSavedIds = Array.isArray(savedOrder)
      ? savedOrder.filter((id) => defaultIds.includes(id))
      : []

    return [...new Set([...validSavedIds, ...defaultIds])]
  } catch {
    return defaultIds
  }
}

const learningStages = computed(() => {
  const order = getSavedStageOrder()
  return order
    .map((id) => defaultLearningStages.find((stage) => stage.id === id))
    .filter(Boolean)
})

const checkInDays = ref([1, 2, 3, 5, 6, 7, 8])
const hasCheckedInToday = computed(() => checkInDays.value.includes(today))

const getConsecutiveCheckInDays = (days, anchorDay) => {
  if (anchorDay <= 0) {
    return 0
  }

  const checkedSet = new Set(days)
  let count = 0

  while (checkedSet.has(anchorDay - count)) {
    count += 1
  }

  return count
}

const consecutiveCheckInDays = computed(() => {
  const anchorDay = hasCheckedInToday.value ? today : today - 1
  return getConsecutiveCheckInDays(checkInDays.value, anchorDay)
})

const showCheckInToast = (message) => {
  checkInToastMessage.value = message
  window.clearTimeout(checkInToastTimer)
  checkInToastTimer = window.setTimeout(() => {
    checkInToastMessage.value = ''
  }, 2000)
}

const handleCheckIn = () => {
  if (hasCheckedInToday.value) {
    showCheckInToast(`已连续签到${consecutiveCheckInDays.value}天`)
    return
  }

  checkInDays.value = [...new Set([...checkInDays.value, today])].sort((a, b) => a - b)
  showCheckInToast(`签到成功，已连续签到${consecutiveCheckInDays.value}天`)
}

const calendarDays = computed(() => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const days = new Array(firstDay).fill(0)
  for (let i = 1; i <= daysInMonth; i++) { days.push(i) }
  return days
})

const defaultProgressCards = [
  { id: 'calendar' },
  { id: 'weekly-time' },
  { id: 'vocab' },
]

const getSavedProgressCardOrder = () => {
  const defaultIds = defaultProgressCards.map((item) => item.id)

  try {
    const savedOrder = JSON.parse(window.localStorage.getItem(PROGRESS_ORDER_STORAGE_KEY) || '[]')
    const validSavedIds = Array.isArray(savedOrder)
      ? savedOrder.filter((id) => defaultIds.includes(id))
      : []

    return [...new Set([...validSavedIds, ...defaultIds])]
  } catch {
    return defaultIds
  }
}

const progressCards = computed(() => {
  const order = getSavedProgressCardOrder()
  return order
    .map((id) => defaultProgressCards.find((item) => item.id === id))
    .filter(Boolean)
})

const chartRef = ref(null)
const mobileChartRef = ref(null)
let desktopChart = null
let mobileChart = null

const chartOption = {
  grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: '#888', fontSize: 12 }
  },
  yAxis: {
    type: 'value',
    splitLine: { show: false },
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: '#888', fontSize: 12 }
  },
  tooltip: {
    trigger: 'axis',
    backgroundColor: 'rgba(45, 52, 54, 0.95)',
    borderWidth: 0,
    textStyle: { color: '#fff', fontSize: 12 },
    formatter: '{b}: {c}分钟'
  },
  series: [{
    type: 'line',
    smooth: true,
    symbol: 'circle',
    symbolSize: 8,
    lineStyle: { color: '#FF8A3D', width: 3 },
    itemStyle: { color: '#FF8A3D', borderWidth: 2, borderColor: '#fff' },
    areaStyle: {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: 'rgba(255, 138, 61, 0.4)' },
        { offset: 1, color: 'rgba(255, 138, 61, 0)' }
      ])
    },
    data: [25, 30, 20, 45, 35, 50, 40]
  }]
}

const initChart = () => {
  if (!chartRef.value) return
  if (!desktopChart) {
    desktopChart = echarts.init(chartRef.value)
  }
  desktopChart.setOption(chartOption)
  desktopChart.resize()
}

const initMobileChart = () => {
  const chartEl = Array.isArray(mobileChartRef.value) ? mobileChartRef.value[0] : mobileChartRef.value
  if (!chartEl) return
  if (!mobileChart) {
    mobileChart = echarts.init(chartEl)
  }
  mobileChart.setOption(chartOption)
  mobileChart.resize()
}

const handleResize = () => {
  desktopChart?.resize()
  mobileChart?.resize()
}

const scrollPageToTop = () => {
  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0

  if (pageRef.value) {
    pageRef.value.scrollTop = 0
  }
}

onMounted(() => {
  syncMobileTabFromRoute()
  nextTick(() => {
    initChart()
    initMobileChart()
  })
  window.addEventListener('resize', handleResize)
})

watch(
  () => route.query.tab,
  () => {
    syncMobileTabFromRoute()
  }
)

watch(activeMobileTab, async (tab) => {
  await nextTick()
  scrollPageToTop()

  if (tab === 'progress') {
    initMobileChart()
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.clearTimeout(checkInToastTimer)
  desktopChart?.dispose()
  mobileChart?.dispose()
})
</script>

<style scoped>
/* 移动端菜单按钮 */
.mobile-menu-btn {
  display: none;
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 1001;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  color: #FF8A3D;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.mobile-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 998;
}

.index-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #FDFBF7 0%, #F5F0E8 50%, #FAF8F5 100%);
  position: relative;
  overflow: hidden;
  color: #2d3436;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.mobile-shell {
  display: none;
}

.mobile-screen {
  position: relative;
  width: 100%;
  min-height: 100vh;
  padding: 20px 20px 110px;
}

.mobile-bg-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.55;
  pointer-events: none;
}

.mobile-bg-glow-1 {
  width: 220px;
  height: 220px;
  top: 60px;
  left: -80px;
  background: rgba(255, 220, 176, 0.8);
}

.mobile-bg-glow-2 {
  width: 240px;
  height: 240px;
  right: -90px;
  top: 220px;
  background: rgba(214, 236, 255, 0.9);
}

.mobile-topbar {
  display: flex;
  align-items: center;
  gap: 14px;
}

.mobile-search {
  flex: 1;
  min-height: 52px;
  padding: 0 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 12px 32px rgba(140, 104, 68, 0.08);
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-search-icon {
  color: #9a9a9a;
  font-size: 15px;
}

.mobile-search-input {
  flex: 1;
  border: none;
  background: transparent;
  color: #2d3436;
  font-size: 15px;
  outline: none;
}

.mobile-search-input::placeholder {
  color: #aaa;
}

.mobile-avatar-btn {
  width: 52px;
  height: 52px;
  border: none;
  border-radius: 50%;
  padding: 0;
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 12px 28px rgba(140, 104, 68, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-avatar-thumb {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f6f1eb 0%, #fff 100%);
  color: #ff8a3d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.mobile-hero {
  margin-top: 28px;
}

.mobile-hero-title {
  font-size: 24px;
  font-weight: 800;
  color: #20252d;
  line-height: 1.2;
}

.mobile-hero-subtitle {
  margin-top: 10px;
  color: #6f7580;
  font-size: 15px;
}

.mobile-stage-list {
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-stage-card {
  position: relative;
  overflow: hidden;
  min-height: 124px;
  padding: 18px 18px 18px 16px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.92);
  box-shadow: 0 18px 40px rgba(133, 110, 86, 0.12);
  display: grid;
  grid-template-columns: 76px 1fr 56px;
  align-items: center;
  gap: 14px;
  cursor: pointer;
}

.mobile-stage-card::before {
  content: '';
  position: absolute;
  right: -18px;
  top: -12px;
  width: 128px;
  height: 128px;
  border-radius: 50%;
  background: radial-gradient(circle, color-mix(in srgb, var(--mobile-stage-color) 20%, white) 0%, rgba(255, 255, 255, 0) 72%);
  opacity: 0.9;
}

.mobile-stage-visual,
.mobile-stage-copy,
.mobile-stage-play {
  position: relative;
  z-index: 1;
}

.mobile-stage-icon {
  width: 76px;
  height: 76px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--mobile-stage-color);
  font-size: 34px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.mobile-stage-age {
  display: inline-block;
  color: var(--mobile-stage-color);
  font-size: 14px;
  font-weight: 800;
}

.mobile-stage-title {
  margin-top: 6px;
  font-size: 18px;
  font-weight: 800;
  color: #252933;
  line-height: 1.25;
}

.mobile-stage-desc {
  margin-top: 6px;
  color: #535965;
  font-size: 14px;
  line-height: 1.45;
}

.mobile-stage-play {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.96);
  color: var(--mobile-stage-color);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.mobile-progress-head {
  padding-top: 8px;
}

.mobile-section-heading {
  font-size: 28px;
  font-weight: 800;
  color: #20252d;
}

.mobile-panel-card {
  margin-top: 18px;
  padding: 20px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.94);
  box-shadow: 0 18px 40px rgba(133, 110, 86, 0.11);
}

.mobile-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.mobile-panel-head-chart {
  margin-bottom: 8px;
}

.mobile-panel-title {
  color: #20252d;
  font-size: 17px;
  font-weight: 800;
}

.mobile-panel-badge {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 170, 102, 0.16);
  color: #da9954;
  font-size: 13px;
  font-weight: 700;
}

.mobile-panel-meta {
  color: #8d9098;
  font-size: 13px;
  font-weight: 600;
}

.mobile-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px 4px;
}

.mobile-calendar-weekday {
  text-align: center;
  color: #8b8f97;
  font-size: 13px;
  font-weight: 700;
}

.mobile-calendar-day {
  min-height: 40px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: #59606c;
}

.mobile-calendar-day.is-empty {
  visibility: hidden;
}

.mobile-calendar-number {
  font-size: 14px;
  font-weight: 700;
}

.mobile-calendar-star {
  color: #f4ba3c;
  font-size: 13px;
}

.mobile-calendar-day.is-today {
  background: rgba(92, 202, 120, 0.16);
}

.mobile-calendar-day.is-today .mobile-calendar-number {
  color: #23af56;
}

.mobile-check-in-btn {
  width: 100%;
  min-height: 46px;
  margin-top: 18px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8b30 0%, #ff711b 100%);
  color: #ffffff;
  font-size: 15px;
  font-weight: 800;
  box-shadow: 0 12px 24px rgba(255, 123, 33, 0.2);
}

.mobile-check-in-btn.checked {
  background: rgba(90, 198, 116, 0.16);
  color: #28a552;
  box-shadow: none;
}

.mobile-check-in-btn:disabled {
  cursor: default;
}

.mobile-chart-container {
  width: 100%;
  height: 220px;
}

.mobile-vocab-card {
  position: relative;
  overflow: hidden;
}

.mobile-vocab-number {
  margin-top: 20px;
  font-size: 56px;
  line-height: 1;
  font-weight: 800;
  color: #20252d;
}

.mobile-vocab-deco {
  position: absolute;
  opacity: 0.9;
}

.mobile-vocab-deco-left {
  left: 16px;
  bottom: 18px;
  color: #9cb9ff;
  font-size: 34px;
}

.mobile-vocab-deco-top {
  right: 20px;
  top: 62px;
  color: #f6b59a;
  font-size: 30px;
  transform: rotate(-12deg);
}

.mobile-vocab-deco-right {
  right: 18px;
  top: 18px;
  color: #ffc37a;
  font-size: 34px;
}

.mobile-vocab-deco-bottom {
  right: 24px;
  bottom: 12px;
  color: #7bc45d;
  font-size: 36px;
}

.mobile-profile-top {
  padding-top: 10px;
  text-align: center;
}

.mobile-profile-avatar {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.mobile-profile-avatar-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  border: 4px solid rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 40px rgba(133, 110, 86, 0.12);
  color: #ff8a3d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 38px;
}

.mobile-profile-name {
  margin-top: 18px;
  color: #20252d;
  font-size: 22px;
  font-weight: 800;
}

.mobile-membership-card {
  margin-top: 14px;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(90deg, #f9d4bc 0%, #f8e9dd 100%);
  box-shadow: 0 16px 36px rgba(217, 157, 112, 0.16);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  text-align: left;
}

.mobile-membership-copy {
  min-width: 0;
}

.mobile-membership-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #9f5f31;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
}

.mobile-membership-crown {
  font-size: 15px;
  color: #b56b37;
}

.mobile-membership-expiry {
  margin-top: 8px;
  color: rgba(159, 95, 49, 0.72);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.mobile-membership-link {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 0;
  color: #9f5f31;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.mobile-profile-menu {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-profile-item {
  width: 100%;
  min-height: 76px;
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 18px 40px rgba(133, 110, 86, 0.1);
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
}

.mobile-profile-item-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--profile-accent) 16%, white);
  color: var(--profile-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.mobile-profile-item-label {
  flex: 1;
  color: #20252d;
  font-size: 17px;
  font-weight: 700;
}

.mobile-profile-item-arrow {
  color: #8e939c;
  font-size: 14px;
}

.mobile-tabbar {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  height: 84px;
  padding: 10px 8px 12px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.96);
  box-shadow: 0 22px 44px rgba(133, 110, 86, 0.18);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  z-index: 20;
}

.mobile-tabbar-item {
  border: none;
  background: transparent;
  color: #8d9199;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
}

.mobile-tabbar-icon {
  font-size: 20px;
}

.mobile-tabbar-item.active {
  color: #ff8a3d;
}

.bg-decoration { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
.orb { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.5; animation: float-orb 8s ease-in-out infinite; }
.orb-1 { width: 400px; height: 400px; background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%); top: -100px; right: -100px; animation-delay: 0s; }
.orb-2 { width: 350px; height: 350px; background: linear-gradient(135deg, #E8F4FF 0%, #D4E8FF 100%); bottom: -50px; left: -100px; animation-delay: -3s; }
.orb-3 { width: 250px; height: 250px; background: linear-gradient(135deg, #FFEAA8 0%, #FFD88A 100%); top: 50%; left: 30%; animation-delay: -5s; }

@keyframes float-orb {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

.glass-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
}

.content-wrapper {
  display: flex;
  height: 100vh;
  position: relative;
  z-index: 10;
}

/* 左侧导航 */
.sidebar { 
  width: 280px; 
  flex-shrink: 0; 
  height: 100vh;
  overflow-y: auto;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
}
.nav-card { 
  min-height: 100%;
  padding: 0 0 20px;
  display: flex;
  flex-direction: column;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}
.nav-logo .logo-icon {
  width: 48px; height: 48px;
  background: #fff;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.nav-logo .brand-name {
  font-size: 22px; font-weight: 800; color: #2D3436; letter-spacing: 2px;
}

.nav-list { 
  list-style: none; 
  display: flex; 
  flex-direction: column; 
  gap: 20px; 
  padding: 16px 12px 0;
}
.nav-item {
  display: flex; align-items: center; gap: 14px;
  padding: 18px 18px; border-radius: 14px;
  color: #666; font-size: 15px; font-weight: 500;
  transition: all 0.3s ease; cursor: pointer;
  position: relative;
  text-decoration: none;
}
.nav-item:hover { 
  background: rgba(255, 138, 61, 0.08); 
  color: #FF8A3D;
}
.nav-item.active { 
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%); 
  color: #FF6B35; 
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}
.nav-icon { font-size: 18px; width: 24px; text-align: center; }

/* 右侧内容 */
.right-wrapper { 
  flex: 1; 
  display: flex; 
  flex-direction: column; 
  padding: 16px;
  overflow: hidden;
}

.welcome-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  margin-bottom: 16px;
  flex-shrink: 0;
}
.welcome-left { flex: 1; }
.welcome-text { font-size: 22px; font-weight: 700; color: #2D3436; }
.wave { display: inline-block; animation: wave 1s ease-in-out infinite; }
@keyframes wave { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(20deg); } 75% { transform: rotate(-20deg); } }
.date-text { font-size: 14px; color: #888; margin-top: 6px; }

.top-membership-card {
  min-width: 272px;
  flex-shrink: 0;
  padding: 10px 12px 10px 18px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 248, 243, 0.96) 0%, rgba(255, 244, 236, 0.92) 100%);
  border: 1px solid rgba(255, 151, 83, 0.28);
  box-shadow:
    0 10px 24px rgba(209, 145, 93, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.top-membership-copy {
  min-width: 0;
}

.top-membership-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #db7420;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
}

.top-membership-crown {
  color: #f0a63b;
  font-size: 14px;
}

.top-membership-expiry {
  margin-top: 6px;
  color: #a08471;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 600;
}

.top-membership-btn {
  min-width: 126px;
  height: 48px;
  padding: 0 22px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8b2f 0%, #ff6d16 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 18px rgba(255, 122, 24, 0.26);
  transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;
}

.top-membership-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(255, 122, 24, 0.3);
  filter: saturate(1.04);
}

.top-membership-btn:active {
  transform: scale(0.98);
}

/* 内容区域 */
.content-area {
  flex: 1;
  display: flex;
  gap: 20px;
  overflow: hidden;
}

.left-content { flex: 1; overflow-y: auto; }
.right-panel {
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 44px;
  box-sizing: border-box;
  overflow-y: auto;
}

.section-card { margin-bottom: 20px; overflow: visible; }
.section-title { font-size: 18px; font-weight: 700; color: #2D3436; margin-bottom: 16px; }

.stage-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  overflow: visible;
}
.stage-card {
  position: relative;
  display: flex; align-items: flex-start; gap: 16px;
  min-height: 136px;
  padding: 24px 20px; border-radius: 20px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 10px 24px rgba(196, 146, 92, 0.08);
  transition: all 0.3s ease; cursor: pointer;
  isolation: isolate;
}
.stage-card::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease, box-shadow 0.3s ease;
}
.stage-card:hover {
  transform: translateY(-4px);
}
.stage-card:hover::after {
  opacity: 1;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.86),
    0 14px 30px rgba(255, 138, 61, 0.10),
    0 0 34px rgba(255, 138, 61, 0.16);
}
.stage-icon {
  width: 52px; height: 52px; border-radius: 16px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; color: var(--stage-color);
}
.stage-title { font-size: 16px; font-weight: 700; color: #2D3436; }
.stage-desc { font-size: 12px; color: #888; margin-top: 4px; }
.stage-tag {
  display: inline-block; margin-top: 8px; padding: 4px 10px;
  border-radius: 50px; font-size: 11px; font-weight: 600;
}

.data-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.data-card, .chart-card { padding: 20px; }
.data-title { font-size: 14px; color: #888; font-weight: 500; margin-bottom: 12px; }
.data-content { display: flex; justify-content: space-between; align-items: flex-end; }
.number { font-size: 42px; font-weight: 800; color: #2D3436; }
.growth { display: block; font-size: 13px; color: #4CAF50; font-weight: 600; margin-top: 4px; }
.data-deco { font-size: 52px; color: #FF8A3D; opacity: 0.15; }
.chart-container { width: 100%; height: 140px; }

.calendar-card {
  padding: 18px;
  box-shadow: none;
}
.calendar-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.calendar-title { font-size: 15px; font-weight: 700; color: #2D3436; }
.calendar-month { font-size: 12px; color: #FF8A3D; font-weight: 600; }
.calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; text-align: center; }
.calendar-weekday { font-size: 11px; color: #AAA; font-weight: 500; padding: 4px 0; }
.calendar-day {
  font-size: 11px; color: #666; padding: 6px 2px; border-radius: 8px;
  transition: all 0.2s; cursor: pointer;
}
.calendar-day:hover:not(.is-empty) { background: rgba(0, 0, 0, 0.05); }
.calendar-day.is-today { background: #FF8A3D; color: white; font-weight: 700; }
.calendar-day.is-checked { background: rgba(76, 175, 80, 0.15); color: #4CAF50; }
.calendar-day.is-empty { cursor: default; }
.streak-tip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  padding: 10px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 12px;
}
.streak-main { display: flex; align-items: center; gap: 10px; min-width: 0; }
.streak-icon { font-size: 20px; color: #FF8A3D; }
.streak-count { display: block; font-size: 13px; font-weight: 700; color: #4CAF50; }
.streak-hint { font-size: 11px; color: #888; }

.check-in-btn {
  flex-shrink: 0;
  min-width: 92px;
  min-height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8b30 0%, #ff711b 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 18px rgba(255, 123, 33, 0.18);
  transition: transform 0.22s ease, box-shadow 0.22s ease, opacity 0.22s ease;
}

.check-in-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(255, 123, 33, 0.24);
}

.check-in-btn.checked {
  background: rgba(90, 198, 116, 0.16);
  color: #28a552;
  box-shadow: none;
}

.check-in-btn:disabled {
  cursor: default;
  transform: none;
}

.checkin-toast {
  position: fixed;
  left: 50%;
  bottom: 32px;
  transform: translateX(-50%);
  z-index: 2200;
  max-width: min(88vw, 420px);
  padding: 12px 18px;
  border-radius: 999px;
  background: rgba(45, 52, 54, 0.92);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.checkin-toast-enter-active,
.checkin-toast-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.checkin-toast-enter-from,
.checkin-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 10px);
}

.tree-card {
  padding: 20px;
  text-align: center;
  box-shadow: none;
}

/* 顶部区域 */
.tree-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}
.tree-title { font-size: 16px; font-weight: 700; color: #2D3436; }
.level-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.level-badge {
  padding: 4px 12px;
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%);
  color: white;
  font-size: 12px;
  font-weight: 700;
  border-radius: 50px;
}
.level-name {
  font-size: 11px;
  color: #999;
}

/* 中间展示区 */
.tree-showcase {
  position: relative;
  height: 120px;
  margin-bottom: 12px;
}

.particles { position: absolute; inset: 0; }
.particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: #FFD700;
  border-radius: 50%;
  animation: sparkle 2s ease-in-out infinite;
}
.particle.p1 { top: 20%; left: 15%; animation-delay: 0s; }
.particle.p2 { top: 30%; right: 20%; animation-delay: 0.5s; }
.particle.p3 { top: 50%; left: 10%; animation-delay: 1s; }
.particle.p4 { top: 40%; right: 15%; animation-delay: 1.5s; }
@keyframes sparkle {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.5); }
}

.tree-illustration {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
}

.tree-plant {
  position: relative;
  width: 50px;
  height: 70px;
  animation: sway 3s ease-in-out infinite;
}
@keyframes sway {
  0%, 100% { transform: rotate(-2deg); }
  50% { transform: rotate(2deg); }
}

/* 树苗茎干 */
.tree-stem {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 5px;
  height: 40px;
  background: linear-gradient(to top, #6D4C41, #81C784);
  border-radius: 3px;
}

/* 顶部嫩芽 */
.tree-bud {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 10px;
  height: 14px;
  background: linear-gradient(to top, #66BB6A, #81C784);
  border-radius: 50% 50% 40% 40%;
}

/* 左侧叶子 */
.leaf-left {
  position: absolute;
  top: 15px;
  left: -8px;
  width: 22px;
  height: 14px;
  background: linear-gradient(135deg, #81C784 0%, #66BB6A 100%);
  border-radius: 50% 0 50% 50%;
  transform: rotate(-20deg);
}

/* 右侧叶子 */
.leaf-right {
  position: absolute;
  top: 20px;
  right: -8px;
  width: 22px;
  height: 14px;
  background: linear-gradient(135deg, #66BB6A 0%, #81C784 100%);
  border-radius: 0 50% 50% 50%;
  transform: rotate(20deg);
}

/* 中间叶子 */
.leaf-center {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 18px;
  height: 12px;
  background: linear-gradient(to top, #66BB6A, #A5D6A7);
  border-radius: 50% 50% 50% 50%;
}

/* 泥土 */
.ground {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: 12px;
  background: linear-gradient(to bottom, #5D4037, #4E342E);
  border-radius: 50%;
}

/* 文案说明 */
.tree-slogan {
  font-size: 13px;
  color: #999;
  margin-bottom: 16px;
}

/* 成长进度 */
.progress-section { margin-bottom: 16px; }
.progress-header { display: flex; justify-content: space-between; margin-bottom: 8px; }
.progress-label { font-size: 13px; color: #666; }
.progress-value { font-size: 13px; font-weight: 700; color: #4CAF50; }
.progress-bar { height: 8px; background: #F0F0F0; border-radius: 4px; overflow: hidden; }
.progress-fill { height: 100%; background: linear-gradient(90deg, #4CAF50 0%, #66BB6A 100%); border-radius: 4px; transition: width 1s ease; animation: progressGrow 1.5s ease-out; }
@keyframes progressGrow {
  from { width: 0 !important; }
}
.learn-btn {
  width: 100%; padding: 14px;
  background: linear-gradient(135deg, #FF8A3D 0%, #FF6B35 100%);
  border: none; border-radius: 24px; color: white;
  font-size: 15px; font-weight: 700; cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.3);
  position: relative;
  overflow: hidden;
}
.learn-btn:hover { transform: scale(1.02); box-shadow: 0 6px 20px rgba(255, 138, 61, 0.4); }
.learn-btn:active { transform: scale(0.98); }

.learn-btn.watering {
  pointer-events: none;
}

.watering-animation {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
}

.watering-text {
  animation: pulse 0.6s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* 浇水动画层 */
.watering-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.watering-scene {
  position: relative;
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tree-grow {
  animation: treeShake 0.5s ease-in-out infinite;
}

@keyframes treeShake {
  0%, 100% { transform: rotate(-2deg); }
  50% { transform: rotate(2deg); }
}

/* 小树样式 */
.mini-tree {
  position: relative;
  width: 60px;
  height: 80px;
}

.mini-crown {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 45px;
  background: radial-gradient(ellipse at center, #81C784 0%, #66BB6A 60%, #4CAF50 100%);
  border-radius: 50% 50% 45% 45%;
  box-shadow: inset -5px -5px 10px rgba(0,0,0,0.1), inset 3px 3px 8px rgba(255,255,255,0.3);
}

.mini-crown::before {
  content: '';
  position: absolute;
  top: 6px;
  left: 8px;
  width: 12px;
  height: 10px;
  background: rgba(255,255,255,0.3);
  border-radius: 50%;
}

.mini-trunk {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 30px;
  background: linear-gradient(to right, #6D4C41 0%, #8D6E63 50%, #6D4C41 100%);
  border-radius: 2px;
}

.mini-ground {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 10px;
  background: linear-gradient(to bottom, #5D4037, #4E342E);
  border-radius: 50%;
}

/* 小水壶 */
.mini-watering-can {
  position: absolute;
  right: 10px;
  top: 25px;
  animation: pourWater 0.4s ease-in-out infinite alternate;
}

@keyframes pourWater {
  0% { transform: rotate(-12deg); }
  100% { transform: rotate(-20deg); }
}

.mini-can-body {
  width: 32px;
  height: 28px;
  background: linear-gradient(135deg, #4FC3F7 0%, #29B6F6 50%, #03A9F4 100%);
  border-radius: 4px 4px 8px 8px;
  position: relative;
  box-shadow: inset -2px -2px 4px rgba(0,0,0,0.15), inset 2px 2px 4px rgba(255,255,255,0.3);
}

.mini-can-body::before {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 6px;
  height: 16px;
  background: rgba(255,255,255,0.35);
  border-radius: 3px;
}

/* 壶嘴 */
.mini-can-spout {
  position: absolute;
  top: 6px;
  left: -14px;
  width: 16px;
  height: 7px;
  background: linear-gradient(135deg, #4FC3F7, #29B6F6);
  border-radius: 4px 0 0 4px;
  transform: rotate(-30deg);
}

/* 壶嘴出水口 */
.mini-can-spout::after {
  content: '';
  position: absolute;
  left: -3px;
  top: -1px;
  width: 6px;
  height: 9px;
  background: linear-gradient(135deg, #4FC3F7, #29B6F6);
  border-radius: 2px;
  transform: rotate(5deg);
}

/* 手柄 */
.mini-can-handle {
  position: absolute;
  top: 2px;
  right: -8px;
  width: 10px;
  height: 20px;
  border: 3px solid #29B6F6;
  border-left: none;
  border-radius: 0 6px 6px 0;
  background: transparent;
}

/* 水流 - 从壶嘴流出 */
.mini-water-flow {
  position: absolute;
  top: 18px;
  left: -20px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  animation: waterFlow 0.4s linear infinite;
}

@keyframes waterFlow {
  0% { opacity: 1; transform: translateY(0) rotate(-30deg); }
  100% { opacity: 0; transform: translateY(25px) rotate(-30deg); }
}

.mini-drop {
  width: 5px;
  height: 10px;
  background: linear-gradient(180deg, #81D4FA, #4FC3F7);
  border-radius: 50% 50% 50% 50%;
  animation: dropFall 0.6s ease-in infinite;
}

.mini-drop:nth-child(1) { animation-delay: 0s; }
.mini-drop:nth-child(2) { animation-delay: 0.15s; }
.mini-drop:nth-child(3) { animation-delay: 0.3s; }

@keyframes dropFall {
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.5); }
}

/* 响应式 */
@media (max-width: 1200px) {
  .sidebar { width: 236px; }
  .right-panel { width: 260px; }
}
@media (max-width: 992px) {
  .sidebar { width: 216px; }
  .content-wrapper { gap: 16px; }
  .right-panel { 
    width: 100%; 
    flex-direction: row;
    overflow-x: auto;
    gap: 12px;
    padding-bottom: 16px;
  }
  .right-panel > * { min-width: 280px; flex-shrink: 0; }
}
@media (max-width: 768px) {
  .mobile-shell {
    display: block;
    position: relative;
    min-height: 100vh;
    z-index: 12;
  }

  .content-wrapper,
  .mobile-menu-btn,
  .mobile-overlay {
    display: none !important;
  }

  .bg-decoration {
    opacity: 0.5;
  }

  .index-page {
    overflow-y: auto;
    padding-bottom: 12px;
  }
  
  .sidebar {
    position: fixed;
    left: -280px;
    top: 0;
    z-index: 999;
    transition: left 0.3s ease;
    width: 280px;
  }
  .sidebar.mobile-open { left: 0; }
  
  .content-wrapper { 
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    gap: 0;
  }
  
  .right-wrapper { 
    padding: 70px 16px 24px; 
    width: 100%;
    min-height: 0;
  }
  
  .welcome-bar { 
    flex-direction: column; 
    gap: 16px; 
    text-align: center;
    margin-bottom: 16px;
    flex-shrink: 0;
  }
  .welcome-left { display: flex; flex-direction: column; align-items: center; }
  .welcome-text { font-size: 18px; }
  .content-area { 
    flex-direction: column;
    width: 100%;
    gap: 20px;
  }
  .left-content { width: 100%; }
  
  .right-panel { 
    width: 100%; 
    flex-direction: column;
    gap: 16px;
    padding-top: 0;
  }
  
  .stage-grid { grid-template-columns: 1fr; }
  .data-grid { grid-template-columns: 1fr; }
  
  .nav-logo { padding: 16px; gap: 10px; }
  .nav-logo .logo-icon { width: 40px; height: 40px; font-size: 20px; }
  .nav-logo .brand-name { font-size: 18px; }
  
  .index-page { overflow-x: hidden; }
  
  .chart-container { width: 100% !important; height: 120px !important; }
  .chart-card { overflow: hidden; }
}
</style>
